import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { contactosApi, entidadesApi, referenciaApi, catalogosApi, contratosApi, exportApi, aiApi } from '../services/api';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Search, Users as UsersIcon, Mail, Phone, MapPin, Briefcase, FileStack, Star, Upload, Plus, Download, Sparkles, Pencil, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import ImportadorCSV from '../components/ImportadorCSV';
import AiButton from '../components/AiButton';
import EmpresaAutocomplete from '../components/empresas/EmpresaAutocomplete';
import { useDebounce } from '../hooks/useDebounce';
import { useSortable } from '../hooks/useSortable';
import { SortableHeader } from '../components/ui/SortableHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { isValidEmail } from '../utils/validation';

const NIVEL_COLOR = {
  verde: 'bg-emerald-500',
  amarillo: 'bg-amber-400',
  rojo: 'bg-red-500',
  sin_definir: 'bg-ink-300',
};
const NIVEL_TITLE = {
  verde: 'Amistad estrecha',
  amarillo: 'Cordial',
  rojo: 'Debe mejorar',
  sin_definir: 'Sin definir',
};

const ContactosGlobal = () => {
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  const canExport = user?.rol === 'admin' || user?.rol === 'dir_comercial';
  const [importDialog, setImportDialog] = useState(false);
  const [nuevoDialog, setNuevoDialog] = useState({ open: false, data: null });
  const [dupAviso, setDupAviso] = useState(null);
  const [editDialog, setEditDialog] = useState({ open: false, contacto: null });
  const [entidadBuscar, setEntidadBuscar] = useState('');  // { candidatos_analizados, mejor_match_id, payload }
  const [contactos, setContactos] = useState([]);
  const [entidades, setEntidades] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [provincias, setProvincias] = useState([]);
  const [funciones, setFunciones] = useState([]);
  const [depsCat, setDepsCat] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      q: '', entidad_id: '', provincia: '', funcion: '', decisor: '', nivel: '',
      pendiente_revision: params.get('pendiente_revision') === 'true' ? 'true' : '',
    };
  });

  const blankContacto = {
    nombre: '', apellidos: '', entidad_id: '', cargo: '', departamento: '',
    email_trabajo: '', movil_trabajo: '', telefono_trabajo: '',
    es_decisor: false, nivel_relacion: 'sin_definir', notas: '',
  };

  const cargar = async () => {
    try {
      const params = { limit: 1000 };
      if (filters.pendiente_revision === 'true') params.pendiente_revision = true;
      const [cs, ents, provs, fns, cts, deps] = await Promise.all([
        contactosApi.list(params),
        entidadesApi.list({ limit: 1000 }).catch(() => []),
        referenciaApi.provincias().catch(() => []),
        catalogosApi.list('funciones_contacto').catch(() => []),
        contratosApi.list().catch(() => []),
        catalogosApi.list('departamentos_contacto').catch(() => []),
      ]);
      setContactos(cs);
      setEntidades(ents);
      setProvincias(provs);
      setFunciones(fns);
      setContratos(cts);
      setDepsCat(deps);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [filters.pendiente_revision]);

  const guardarEdicion = async () => {
    if (!editDialog.contacto) return;
    try {
      await contactosApi.update(editDialog.contacto.id, {
        entidad_id: editDialog.contacto.entidad_id || null,
      });
      toast.success('Contacto vinculado correctamente');
      setEditDialog({ open: false, contacto: null });
      setEntidadBuscar('');
      cargar();
    } catch (e) { toast.error(e.message); }
  };


  const entidadIdToProvincia = useMemo(() => {
    const m = {};
    entidades.forEach((e) => { m[e.id] = e.provincia; });
    return m;
  }, [entidades]);

  const qDebounced = useDebounce(filters.q, 300);

  const filtered = useMemo(() => contactos.filter((c) => {
    const q = (qDebounced || '').trim().toLowerCase();
    if (q) {
      const hay = `${c.nombre} ${c.apellidos || ''} ${c.cargo || ''} ${c.departamento || ''} ${c.email_trabajo || ''} ${c.entidad_nombre || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (filters.entidad_id && c.entidad_id !== filters.entidad_id) return false;
    if (filters.provincia && entidadIdToProvincia[c.entidad_id] !== filters.provincia) return false;
    if (filters.funcion && !(c.funciones || []).includes(filters.funcion)) return false;
    if (filters.decisor === 'si' && !c.es_decisor) return false;
    if (filters.decisor === 'no' && c.es_decisor) return false;
    if (filters.nivel && (c.nivel_relacion || 'sin_definir') !== filters.nivel) return false;
    return true;
  }), [contactos, filters, entidadIdToProvincia, qDebounced]);

  // Ordenación cliente (M3)
  const { sorted, sortKey, sortDir, toggle } = useSortable(
    filtered,
    'nombre',
    'asc',
    {
      persona: (c) => `${c.nombre || ''} ${c.apellidos || ''}`.trim(),
      empresa: (c) => c.entidad_nombre || '',
      cargo: (c) => c.cargo || '',
      provincia: (c) => entidadIdToProvincia[c.entidad_id] || '',
      contratos: (c) => (c.contratos_ids || []).length,
      contacto: (c) => c.email_trabajo || c.telefono_trabajo || '',
    }
  );

  return (
    <div data-testid="contactos-global">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-heading font-bold text-ink-900">Contactos</h1>
          <p className="text-ink-500 mt-1">
            Todas las personas del CRM — empleados de los clientes, con sus datos profesionales{isAdmin && ' y personales'}.
          </p>
        </div>
        {(user?.rol === 'admin' || user?.rol === 'dir_comercial') && (
          <div className="flex flex-wrap gap-2">
            {canExport && (
              <Button
                variant="outline"
                onClick={async () => { try { await exportApi.descargar('contactos'); toast.success('CSV descargado'); } catch (e) { toast.error(e.message); } }}
                data-testid="btn-export-contactos"
              >
                <Download className="w-4 h-4 mr-1.5" /> Exportar
              </Button>
            )}
            <Button
              variant="outline"
              onClick={async () => {
                if (!sorted.length) { toast.info('No hay contactos visibles para enriquecer.'); return; }
                if (!window.confirm(`Enriquecer con Perplexity los ${sorted.length} contactos visibles? (cargo, departamento, LinkedIn, email)`)) return;
                try {
                  const ids = sorted.slice(0, 100).map((c) => c.id);
                  const r = await perplexityApi.enriquecerContactosMasivo({
                    contacto_ids: ids,
                    modo: 'solo_vacios',
                  });
                  toast.success(`${ids.length} contactos en cola IA · job ${r.job_id.slice(0, 8)}…`);
                } catch (e) { toast.error(e.message); }
              }}
              data-testid="btn-enriquecer-contactos-ia"
              className="border-violet-200 text-violet-700 hover:bg-violet-50"
              title="Encola los contactos visibles (máx 100) para enriquecimiento Perplexity en background"
            >
              <Sparkles className="w-4 h-4 mr-1.5" /> Enriquecer con IA
            </Button>
            <Button
              variant="outline"
              onClick={() => setImportDialog(true)}
              data-testid="btn-importar-contactos"
              className="border-brand-200 text-brand-700 hover:bg-brand-50"
            >
              <Upload className="w-4 h-4 mr-1.5" /> Importar CSV
            </Button>
            <Button
              onClick={() => setNuevoDialog({ open: true, data: blankContacto })}
              data-testid="btn-nuevo-contacto"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Nuevo contacto
            </Button>
          </div>
        )}
      </div>

      {/* Dialog crear contacto manual */}
      <Dialog open={nuevoDialog.open} onOpenChange={(v) => setNuevoDialog({ open: v, data: v ? nuevoDialog.data : null })}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nuevo contacto</DialogTitle></DialogHeader>
          {nuevoDialog.data && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nombre *</Label><Input value={nuevoDialog.data.nombre} onChange={(e) => setNuevoDialog((d) => ({ ...d, data: { ...d.data, nombre: e.target.value } }))} data-testid="nuevo-contacto-nombre" /></div>
                <div><Label>Apellidos</Label><Input value={nuevoDialog.data.apellidos} onChange={(e) => setNuevoDialog((d) => ({ ...d, data: { ...d.data, apellidos: e.target.value } }))} /></div>
              </div>
              <div>
                <Label>Empresa</Label>
                <Select value={nuevoDialog.data.entidad_id || '__none__'} onValueChange={(v) => setNuevoDialog((d) => ({ ...d, data: { ...d.data, entidad_id: v === '__none__' ? '' : v } }))}>
                  <SelectTrigger><SelectValue placeholder="— (sin asignar)" /></SelectTrigger>
                  <SelectContent className="max-h-60"><SelectItem value="__none__">— (sin asignar)</SelectItem>{entidades.map((e) => <SelectItem key={e.id} value={e.id}>{e.nombre_comercial || e.razon_social}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Cargo</Label><Input value={nuevoDialog.data.cargo} onChange={(e) => setNuevoDialog((d) => ({ ...d, data: { ...d.data, cargo: e.target.value } }))} /></div>
                <div>
                  <Label>Departamento</Label>
                  <Select
                    value={nuevoDialog.data.departamento || '__none__'}
                    onValueChange={(v) => setNuevoDialog((d) => ({ ...d, data: { ...d.data, departamento: v === '__none__' ? '' : v } }))}
                  >
                    <SelectTrigger><SelectValue placeholder="— Sin departamento —" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Sin departamento —</SelectItem>
                      {depsCat.map((d) => <SelectItem key={d.valor} value={d.valor}>{d.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Email trabajo</Label><Input type="email" value={nuevoDialog.data.email_trabajo} onChange={(e) => setNuevoDialog((d) => ({ ...d, data: { ...d.data, email_trabajo: e.target.value } }))} /></div>
                <div><Label>Móvil trabajo</Label><Input value={nuevoDialog.data.movil_trabajo} onChange={(e) => setNuevoDialog((d) => ({ ...d, data: { ...d.data, movil_trabajo: e.target.value } }))} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Nivel de relación</Label>
                  <Select value={nuevoDialog.data.nivel_relacion} onValueChange={(v) => setNuevoDialog((d) => ({ ...d, data: { ...d.data, nivel_relacion: v } }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sin_definir">⚪ Sin definir</SelectItem>
                      <SelectItem value="verde">🟢 Amistad estrecha</SelectItem>
                      <SelectItem value="amarillo">🟡 Cordial</SelectItem>
                      <SelectItem value="rojo">🔴 Debe mejorar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={!!nuevoDialog.data.es_decisor} onChange={(e) => setNuevoDialog((d) => ({ ...d, data: { ...d.data, es_decisor: e.target.checked } }))} />
                    Es decisor
                  </label>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setNuevoDialog({ open: false, data: null })}>Cancelar</Button>
            <Button
              onClick={async () => {
                try {
                  const d = { ...nuevoDialog.data };
                  if (!d.nombre?.trim()) { toast.error('El nombre es obligatorio'); return; }
                  if (!isValidEmail(d.email_trabajo)) { toast.error('Email de trabajo no válido'); return; }
                  if (!d.entidad_id) delete d.entidad_id;
                  // Best-effort duplicate check via Gemini
                  try {
                    const check = await aiApi.detectarDuplicado({
                      nombre: d.nombre,
                      apellidos: d.apellidos || null,
                      email_trabajo: d.email_trabajo || null,
                      movil_trabajo: d.movil_trabajo || null,
                      entidad_id: d.entidad_id || null,
                    });
                    if (check?.es_duplicado_probable) {
                      setDupAviso({ check, payload: d });
                      return; // esperar confirmación del usuario
                    }
                  } catch (_) { /* si falla IA, continúa sin bloquear */ }
                  await contactosApi.create(d);
                  toast.success('Contacto creado');
                  setNuevoDialog({ open: false, data: null });
                  cargar();
                } catch (e) { toast.error(e.message); }
              }}
              data-testid="btn-guardar-nuevo-contacto"
            >
              Crear contacto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog reasignar empresa de contacto */}
      <Dialog open={editDialog.open} onOpenChange={(v) => { if (!v) { setEditDialog({ open: false, contacto: null }); setEntidadBuscar(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Vincular contacto a empresa</DialogTitle>
          </DialogHeader>
          {editDialog.contacto && (
            <div className="space-y-4">
              <p className="text-sm text-ink-600">
                <span className="font-semibold text-ink-900">{editDialog.contacto.nombre} {editDialog.contacto.apellidos || ''}</span>
                {editDialog.contacto.cargo && <span className="text-ink-500"> · {editDialog.contacto.cargo}</span>}
              </p>
              <div className="space-y-2">
                <Label>Empresa</Label>
                <EmpresaAutocomplete
                  value={entidadBuscar}
                  onChange={(v) => {
                    setEntidadBuscar(v);
                    if (!v.trim()) setEditDialog((d) => ({ ...d, contacto: { ...d.contacto, entidad_id: '' } }));
                  }}
                  placeholder="Escribe nombre, CIF o localidad…"
                  onPick={(it) => {
                    setEntidadBuscar(it.razon_social);
                    setEditDialog((d) => ({ ...d, contacto: { ...d.contacto, entidad_id: it.id } }));
                  }}
                />
                {editDialog.contacto.entidad_id && (
                  <button
                    type="button"
                    className="text-xs text-red-500 hover:underline"
                    onClick={() => { setEntidadBuscar(''); setEditDialog((d) => ({ ...d, contacto: { ...d.contacto, entidad_id: '' } })); }}
                  >
                    Quitar empresa
                  </button>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditDialog({ open: false, contacto: null }); setEntidadBuscar(''); }}>Cancelar</Button>
            <Button onClick={guardarEdicion}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal aviso duplicado detectado por Gemini */}
      <Dialog open={!!dupAviso} onOpenChange={(v) => { if (!v) setDupAviso(null); }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-500" /> Posible duplicado detectado
            </DialogTitle>
          </DialogHeader>
          {dupAviso && (
            <div className="space-y-3">
              <p className="text-sm text-ink-600">
                La IA ha encontrado {dupAviso.check.candidatos_analizados?.length || 0} contacto(s) similares.
                Revisa si se trata de la misma persona antes de crear un registro nuevo.
              </p>
              <div className="space-y-2">
                {(dupAviso.check.candidatos_analizados || []).map((c) => {
                  const cand = contactos.find((x) => x.id === c.id);
                  const score = c.score || 0;
                  const color = score >= 85 ? 'bg-rose-100 text-rose-700' : score >= 70 ? 'bg-amber-100 text-amber-700' : 'bg-ink-100 text-ink-600';
                  return (
                    <div key={c.id} className="border border-ink-200 rounded-md p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-ink-900">
                            {cand ? `${cand.nombre} ${cand.apellidos || ''}`.trim() : c.id}
                            {cand?.entidad_nombre && <span className="text-ink-500 font-normal"> · {cand.entidad_nombre}</span>}
                          </p>
                          {cand?.email_trabajo && <p className="text-xs text-ink-500">{cand.email_trabajo}</p>}
                        </div>
                        <span className={`px-2 py-0.5 rounded text-xs ${color}`}>
                          {score}% match
                        </span>
                      </div>
                      <p className="text-xs text-ink-500 mt-1 italic">{c.razon}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDupAviso(null)}>Revisar / Cancelar</Button>
            <Button
              onClick={async () => {
                try {
                  await contactosApi.create(dupAviso.payload);
                  toast.success('Contacto creado igualmente');
                  setDupAviso(null);
                  setNuevoDialog({ open: false, data: null });
                  cargar();
                } catch (e) { toast.error(e.message); }
              }}
              data-testid="btn-crear-igualmente"
            >
              Crear de todas formas
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImportadorCSV
        open={importDialog}
        onOpenChange={setImportDialog}
        tipo="contactos"
        titulo="Importar contactos desde CSV"
        onCompleted={cargar}
      />

      <Card className="p-4 mb-6 border-ink-200">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
            <Input
              placeholder="Buscar por nombre, cargo, empresa, email…"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              className="pl-9"
              data-testid="contactos-filter-q"
            />
          </div>
          <select className="h-10 rounded-md border border-ink-200 bg-white text-sm px-3 text-ink-700" value={filters.entidad_id} onChange={(e) => setFilters({ ...filters, entidad_id: e.target.value })} data-testid="contactos-filter-entidad">
            <option value="">Todas las empresas</option>
            {entidades.map((e) => <option key={e.id} value={e.id}>{e.nombre_comercial || e.razon_social}</option>)}
          </select>
          <select className="h-10 rounded-md border border-ink-200 bg-white text-sm px-3 text-ink-700" value={filters.provincia} onChange={(e) => setFilters({ ...filters, provincia: e.target.value })}>
            <option value="">Todas las provincias</option>
            {provincias.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select className="h-10 rounded-md border border-ink-200 bg-white text-sm px-3 text-ink-700" value={filters.funcion} onChange={(e) => setFilters({ ...filters, funcion: e.target.value })}>
            <option value="">Todas las funciones</option>
            {funciones.map((f) => <option key={f.valor} value={f.valor}>{f.label}</option>)}
          </select>
          <select className="h-10 rounded-md border border-ink-200 bg-white text-sm px-3 text-ink-700" value={filters.decisor} onChange={(e) => setFilters({ ...filters, decisor: e.target.value })}>
            <option value="">Decisor: todos</option>
            <option value="si">Decisor: sí</option>
            <option value="no">Decisor: no</option>
          </select>
          <select className="h-10 rounded-md border border-ink-200 bg-white text-sm px-3 text-ink-700" value={filters.nivel} onChange={(e) => setFilters({ ...filters, nivel: e.target.value })} data-testid="contactos-filter-nivel">
            <option value="">Relación: todas</option>
            <option value="verde">🟢 Amistad estrecha</option>
            <option value="amarillo">🟡 Cordial</option>
            <option value="rojo">🔴 Debe mejorar</option>
            <option value="sin_definir">⚪ Sin definir</option>
          </select>
          <select className="h-10 rounded-md border border-ink-200 bg-white text-sm px-3 text-ink-700" value={filters.pendiente_revision} onChange={(e) => setFilters({ ...filters, pendiente_revision: e.target.value })} data-testid="contactos-filter-pendiente">
            <option value="">Estado revisión</option>
            <option value="true">⚠ Pendientes de revisión</option>
          </select>
        </div>
      </Card>

      <Card className="border-ink-200 overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 border-b border-ink-200 text-ink-600 uppercase text-xs tracking-wider">
              <tr>
                <SortableHeader label="Persona"             sortKey="persona"   currentKey={sortKey} dir={sortDir} onToggle={toggle} />
                <SortableHeader label="Empresa actual"      sortKey="empresa"   currentKey={sortKey} dir={sortDir} onToggle={toggle} />
                <SortableHeader label="Cargo · Depto"       sortKey="cargo"     currentKey={sortKey} dir={sortDir} onToggle={toggle} />
                <SortableHeader label="Provincia"           sortKey="provincia" currentKey={sortKey} dir={sortDir} onToggle={toggle} />
                <SortableHeader label="Contratos asignados" sortKey="contratos" currentKey={sortKey} dir={sortDir} onToggle={toggle} />
                <SortableHeader label="Contacto trabajo"    sortKey="contacto"  currentKey={sortKey} dir={sortDir} onToggle={toggle} />
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100" data-testid="contactos-tbody">
              {loading && <tr><td colSpan={6} className="text-center py-12 text-ink-400">Cargando…</td></tr>}
              {!loading && sorted.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-0 py-0">
                    <EmptyState
                      compact
                      icon={UsersIcon}
                      title="Sin contactos con esos filtros"
                      description="Prueba a quitar la provincia, función o nivel de relación."
                      testid="contactos-empty"
                    />
                  </td>
                </tr>
              )}
              {!loading && sorted.map((c) => (
                <tr
                  key={c.id}
                  className={`hover:bg-ink-50 cursor-pointer ${!c.entidad_nombre ? 'bg-amber-50' : ''}`}
                  onClick={() => c.entidad_id && navigate(`/clientes/${c.entidad_id}`)}
                  data-testid={`contacto-row-${c.id}`}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <div className="h-9 w-9 rounded-full bg-ink-100 text-ink-700 flex items-center justify-center text-sm font-bold">
                          {c.nombre?.[0]?.toUpperCase()}
                        </div>
                        <span
                          title={NIVEL_TITLE[c.nivel_relacion || 'sin_definir']}
                          className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full ring-2 ring-white ${NIVEL_COLOR[c.nivel_relacion || 'sin_definir']}`}
                          data-testid={`nivel-dot-${c.id}`}
                        />
                      </div>
                      <div>
                        <p className="font-semibold text-ink-900 leading-tight flex items-center gap-1">
                          {c.nombre} {c.apellidos || ''}
                          {c.es_decisor && <Star className="w-3 h-3 text-amber-500 fill-amber-500" />}
                        </p>
                        {c.funciones?.length > 0 && (
                          <p className="text-xs text-ink-500 leading-tight">
                            {c.funciones.slice(0, 2).join(' · ')}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-ink-700">
                    <div className="flex items-center justify-between gap-2 group">
                      {c.entidad_nombre ? (
                        <span className="inline-flex items-center gap-1">
                          <Briefcase className="w-3.5 h-3.5 text-ink-400" /> {c.entidad_nombre}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-600 text-xs font-medium">
                          <AlertCircle className="w-3.5 h-3.5" /> Sin empresa
                        </span>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); setEntidadBuscar(c.entidad_nombre || ''); setEditDialog({ open: true, contacto: { ...c } }); }}
                        className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-ink-100 text-ink-400 hover:text-ink-700 transition-opacity"
                        title="Cambiar empresa"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-ink-700">
                    {c.cargo || <span className="text-ink-300">—</span>}
                    {c.departamento && <span className="text-ink-500 text-xs block">· {c.departamento}</span>}
                  </td>
                  <td className="px-4 py-3 text-ink-600 text-sm">
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5 text-ink-400" />
                      {entidadIdToProvincia[c.entidad_id] || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {c.contratos_nombres?.length ? (
                      <div className="flex flex-wrap gap-1">
                        {c.contratos_nombres.slice(0, 2).map((n, i) => (
                          <Badge key={i} variant="outline" className="bg-brand-50 border-brand-200 text-brand-700 text-[10px]">
                            <FileStack className="w-3 h-3 mr-0.5" /> {n}
                          </Badge>
                        ))}
                        {c.contratos_nombres.length > 2 && (
                          <span className="text-[11px] text-ink-500">+{c.contratos_nombres.length - 2}</span>
                        )}
                      </div>
                    ) : <span className="text-ink-300 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-ink-600">
                    {c.email_trabajo && (
                      <p className="flex items-center gap-1 truncate max-w-[200px]"><Mail className="w-3 h-3 text-ink-400" /> {c.email_trabajo}</p>
                    )}
                    {(c.movil_trabajo || c.telefono_trabajo) && (
                      <p className="flex items-center gap-1"><Phone className="w-3 h-3 text-ink-400" /> {c.movil_trabajo || c.telefono_trabajo}</p>
                    )}
                    {!c.email_trabajo && !c.movil_trabajo && !c.telefono_trabajo && <span className="text-ink-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default ContactosGlobal;
